/* eslint-disable max-lines */
import React, { useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import useBookingChangeStep from '../../Hooks/use-booking-change-step';
import useBookingTracking from '../../Hooks/useBookingTracking';
import useLocalize from '../../Hooks/useLocalize';
import { useGetCustomBookingLocalization } from '../../Hooks/use-get-custom-booking-localization';

import { ModalContainer } from '../../SubComponents/Modals';
import { Container, Content, Main, Sidebar } from '../../LayoutComponents/Layout';
import { Button } from '../../SubComponents/Buttons';
import { Row } from '../../SubComponents/Modals';
import Step from '../../ui-components/Step';
import BookingTimer from '../../ui-components/BookingTimer';
import Footer from '../../ui-components/Footer';
import { BookingLocalization } from '../../Utils/graphql-sdk';
import PageMetadata from '../../metadata/page-metadata';
import { STAPE_GTM_ID } from '../../config';
import CustomGTMLoaderHead from '../../ui-components/custom-gtm-loader/head';
import CustomGTMLoaderBody from '../../ui-components/custom-gtm-loader/body';

// Contexts
import BookingStateProvider, { bookingState } from './client-state/booking-state-context';

// Data components
import LocationWithData from './data-components/location-with-data';
import BoatCountWithData from './data-components/boat-count-with-data';
import DurationWithData from './data-components/duration-with-data';
import FlexibleRoutingWithData from './data-components/flexible-routing-with-data';
import CalendarWithData from './data-components/calendar-with-data';
import TimeslotsWithData from './data-components/timeslots-with-data';
import SummaryWithData from './data-components/summary-with-data';
import TermsWithData from './data-components/terms-with-data';
import NewsletterWithData from './data-components/newsletter-with-data';
import SummaryMobileWithData from './data-components/summary-mobile-with-data';
import HeaderWithData from './data-components/header-with-data';
import BookingPaymentModalWrapper from './data-components/checkout-modal';
import CancelationProtectionWithData from './data-components/cancellation-protection-with-data';

// Action types
import { errorReducerActionTypes } from '../../redusers/error-reducer';
import { paymentReducerActionTypes } from '../../redusers/payment-reducer';

// Server data
import useBookingMinimalShop from './server-data/use-booking-minimal-shop';
import useBookingShop from './server-data/use-booking-shop';

// A/B test
import Step3And4WithDataVariant from './data-components/step-3-and-4-with-data-variant';
import ReserveBookingButtonWithData from './data-components/reserve-booking-button-with-data';
import GoToCheckoutButtonWithData from './data-components/go-to-checkout-button-with-data';

export const bookingStepDependencies = {
  1: [],
  2: [
    {
      requirement: 'selectedShopId',
      errorType: errorReducerActionTypes.LOCATION_MISSING,
      errorMessage: 'Location is missing',
    },
  ],
  3: [],
  4: [],
};

const Booking = () => {
  const { data: minimalShop } = useBookingMinimalShop();
  const { data: shop } = useBookingShop();
  const { state, dispatch } = useContext(bookingState);
  const localize = useLocalize();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const getCustomBookingLocalization = useGetCustomBookingLocalization();

  // Setup tracking, useTracking returns a 'track' function to track GTM and FB Pixel events
  const track = useBookingTracking();

  // Change step if conditions are meet
  const changeStep = useBookingChangeStep(bookingStepDependencies);

  const usesNewGTMSetup = shop?.configuration?.gtmId === STAPE_GTM_ID;

  return (
    <>
      {usesNewGTMSetup ? (
        <>
          <CustomGTMLoaderHead domain={shop.websiteRoot.replace('https://', '')} />
          <CustomGTMLoaderBody domain={shop.websiteRoot.replace('https://', '')} />
        </>
      ) : null}
      <PageMetadata
        shopId={minimalShop?.id}
        shopName={minimalShop?.name}
        shopCountry={minimalShop?.country}
      >
        <link rel="canonical" href={`https://${window.location.host}/booking`} />
      </PageMetadata>

      <HeaderWithData />

      <Content>
        <Container isOpen={state.payment.showPaymentModal}>
          <Main>
            {isMobile && !state.payment.isPaid && (
              <BookingTimer
                modifiedDate={state.booking.modifiedDate}
                expiresDate={state.booking.reservedTimeslotExpires}
                mb="3"
              />
            )}

            <Step
              onClick={() => changeStep(1, { onlyBackward: true })}
              currentStep={state.currentStep === 1}
              heading={`1. ${
                getCustomBookingLocalization(
                  BookingLocalization.StepOneBoatsAndDuration
                ) || localize('product.durationHeading')
              }`}
            >
              <Row layout justifyContent="space-between">
                <LocationWithData />

                <BoatCountWithData />
              </Row>

              <DurationWithData />

              <FlexibleRoutingWithData />

              <Row justifyContent="center">
                <Button
                  id="step1NextButton"
                  onClick={() => {
                    changeStep(2);
                    track({ type: 'PRODUCT_VIEW', product: state.booking });
                  }}
                  className={!minimalShop?.id && 'disabled'}
                  look="secondary"
                  size="big"
                >
                  {localize('product.next')}
                </Button>
              </Row>
            </Step>

            <Step
              onClick={() => changeStep(2, { onlyBackward: true })}
              currentStep={state.currentStep === 2}
              heading={`2. ${
                getCustomBookingLocalization(BookingLocalization.StepTwoDateAndTime) ||
                localize('product.dateAndTime')
              }`}
            >
              <Row layout justifyContent="space-between" contentAtTop mb="4">
                <CalendarWithData />

                <TimeslotsWithData />
              </Row>

              <Row style={{ flexDirection: 'column' }}>
                <CancelationProtectionWithData />

                <ReserveBookingButtonWithData />
              </Row>
            </Step>

            <Step3And4WithDataVariant />
          </Main>

          {!isMobile && (
            <Sidebar>
              <>
                <SummaryWithData closeable={false} editable={true} />

                <TermsWithData />

                <NewsletterWithData />

                <GoToCheckoutButtonWithData />
              </>
            </Sidebar>
          )}
        </Container>
      </Content>

      {minimalShop?.country !== 'Australia' && (
        <Footer isOpen={state.payment.showPaymentModal} />
      )}

      {isMobile && <SummaryMobileWithData />}

      <ModalContainer
        isOpen={state.payment.showPaymentModal}
        onBackgroundClick={() => {
          dispatch({
            type: paymentReducerActionTypes.UPDATE_SHOW_PAYMENT_MODAL,
            payload: false,
          });
        }}
        onEscapeKeydown={() => {
          dispatch({
            type: paymentReducerActionTypes.UPDATE_SHOW_PAYMENT_MODAL,
            payload: false,
          });
        }}
        allowScroll={true}
      >
        <Container>
          <Main>
            {isMobile && !state.payment.isPaid && (
              <BookingTimer
                modifiedDate={state.booking.modifiedDate}
                expiresDate={state.booking.reservedTimeslotExpires}
                mb="3"
              />
            )}

            <BookingPaymentModalWrapper />
          </Main>

          {!isMobile && (
            <Sidebar>
              <SummaryWithData closeable={!state.payment.isPaid} editable={false} />
            </Sidebar>
          )}
        </Container>
      </ModalContainer>
    </>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  },
});

const BookingWithState = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BookingStateProvider>
        <Booking />
      </BookingStateProvider>
    </QueryClientProvider>
  );
};

export default BookingWithState;
